import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';
import ModelDocumentationModal from '../../components/shared/ModelDocumentationModal';
import AlertMessage from '../../components/shared/alertMessage';
import { adminApi } from '../../services/api';
import ScopeEditorForm from '../admin/ScopeEditorForm';

const SalesRolesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId } = useParams();
  const gridRef = useRef(null);

  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [alertStateSidePanel, setAlertStateSidePanel] = useState({ show: false, type: 'info', title: '', message: '' });
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ is_active: 'true', search: '', organization: '' });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: 'org_user',
    description: '',
    order: 0,
    is_active: true,
    organization: null
  });
  const [resources, setResources] = useState([]);
  const [showScopeEditor, setShowScopeEditor] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [filters]);

  useEffect(() => {
    if (location.state?.mode === 'create') {
      setIsCreateMode(true);
      setSelectedRole(null);
      setShowPanel(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (roleId && roles.length > 0) {
      const role = roles.find(r => r.id === roleId);
      if (role) handleRowClick({ data: role });
    }
  }, [roleId, roles]);

  const fetchOrganizations = async () => {
    try {
      const res = await adminApi.getOrganizations({ is_active: 'true' });
      setOrganizations(res.data?.organizations || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load organizations', message: err.response?.data?.message || err.message });
    }
  };

  const fetchResources = async () => {
    try {
      const response = await adminApi.get('/admin/scopes/resources/');
      if (response.data) {
        const resourcesArray = [];
        Object.entries(response.data).forEach(([categoryName, resourcesList]) => {
          if (Array.isArray(resourcesList)) {
            resourcesList.forEach(resourceInfo => {
              resourcesArray.push({
                id: resourceInfo.resource,
                resource: resourceInfo.resource,
                description: resourceInfo.description || '',
                category: resourceInfo.category || categoryName,
                visibility: resourceInfo.visibility !== false,
                cruds: resourceInfo.cruds || '',
                views: resourceInfo.views || []
              });
            });
          }
        });
        setResources(resourcesArray);
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const params = {
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };

      // Fetch both org_user and other_org_user categories
      const [orgRoles, otherOrgRoles] = await Promise.all([
        adminApi.get('/admin/roles/', { params: { ...params, category: 'org_user' } }),
        adminApi.get('/admin/roles/', { params: { ...params, category: 'other_org_user' } })
      ]);
      const combinedRoles = [...(orgRoles.data?.roles || []), ...(otherOrgRoles.data?.roles || [])];
      setRoles(combinedRoles);
      setError(null);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load roles', message: err.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = useCallback((event) => {
    setIsCreateMode(false);
    setSelectedRole(event.data);
    setShowPanel(true);
    navigate(`/admin/sales/roles/${event.data.id}`, { replace: true });
  }, [navigate]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedRole(null);
    setIsCreateMode(false);
    setFormData({
      name: '',
      slug: '',
      category: 'org_user',
      description: '',
      order: 0,
      is_active: true,
      organization: null
    });
    navigate('/admin/sales/roles', { replace: true });
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.organization) {
        payload.organization_id = payload.organization;
        delete payload.organization;
      }
      await adminApi.post('/admin/roles/', payload);
      fetchRoles();
      handleClosePanel();
      alert('Sales role created successfully');
    } catch (err) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Failed to create role', message: err.response?.data?.message || err.message });
    }
  };

  const handleUpdateRole = async () => {
    try {
      const payload = {
        name: selectedRole.name,
        slug: selectedRole.slug,
        category: selectedRole.category,
        description: selectedRole.description,
        order: selectedRole.order,
        is_active: selectedRole.is_active
      };
      if (selectedRole.organization_id) {
        payload.organization_id = selectedRole.organization_id;
      }
      await adminApi.patch(`/admin/roles/${selectedRole.id}/`, payload);
      fetchRoles();
      setAlertStateSidePanel({ show: true, type: 'success', title: 'Success', message: 'Role updated successfully' });
    } catch (err) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Failed to update role', message: err.response?.data?.message || err.message });
    }
  };

  const handleDeleteRole = async () => {
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) return;

    try {
      await adminApi.delete(`/admin/roles/${selectedRole.id}/`);
      fetchRoles();
      handleClosePanel();
      alert('Role deleted successfully');
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to delete role', message: err.response?.data?.message || err.message });
    }
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.patch(`/admin/roles/${data.id}/`, { [colDef.field]: newValue });
      fetchRoles();
    } catch (err) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Failed to update', message: err.response?.data?.message || err.message });
      fetchRoles(); // Revert
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `sales-roles-${new Date().toISOString().split('T')[0]}.csv`
    });
  };

  const columnDefs = [
    { headerName: 'Name', field: 'name', width: 180, editable: true },
    { headerName: 'Slug', field: 'slug', width: 180 },
    {
      headerName: 'Organization',
      field: 'organization_name',
      width: 160,
      valueGetter: (params) => params.data?.organization_name || 'Global'
    },
    {
      headerName: 'Category',
      field: 'category',
      width: 140,
      cellRenderer: (params) => (
        <span className={`badge bg-${params.value === 'org_user' ? 'primary' : 'info'}`}>
          {params.value === 'org_user' ? 'Org User' : 'Other Org'}
        </span>
      )
    },
    { headerName: 'Description', field: 'description', width: 300, editable: true },
    {
      headerName: 'Order',
      field: 'order',
      width: 90,
      editable: true,
      cellEditor: 'agNumberCellEditor'
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: (params) => (
        <span className={`badge bg-${params.value ? 'success' : 'secondary'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Created',
      valueGetter: p => p.data?.created_at ? new Date(p.data.created_at).toLocaleDateString() : '-',
      width: 120
    }
  ];

  return (
    <>
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          dismissible={true}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
        <h4 className="text-lg">
          Sales Roles & Permissions
        </h4>
        <p className="text-muted">Granular Management of Roles and Permissions for Organization Users</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/admin">Sales</a></li>
              <li className="breadcrumb-item"><a href="">User Management</a></li>
              <li className="breadcrumb-item active">Roles</li>
            </ol>
          </nav>
        </div>
        </div>
      <div className="d-flex justify-content-end align-items-center mb-3">
          <button className="btn btn-primary"
            onClick={() => navigate('/admin/sales/roles', { state: { mode: 'create' } })}
          ><i className="fas fa-plus me-1" />Add Role
          </button>
      </div>
      <div className="card">
        <div className="d-flex justify-content-between mb-3 p-2">
          <div className='d-flex gap-2'>
            <select
                className="form-select form-select-sm"
                value={filters.organization}
                onChange={e => setFilters({ ...filters, organization: e.target.value })}
              >
                <option value="">All Organizations (+ Global)</option>
                <option value="__global__">Global Only</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <select
                className="form-select form-select-sm"
                value={filters.is_active}
                onChange={e => setFilters({ ...filters, is_active: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search roles..."
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
            />
              <vr></vr>
              <small className="text-muted">
                Showing {roles.length} role{roles.length !== 1 ? 's' : ''}
              </small>
            </div>
            <div className='d-flex gap-2 justify-content-end'>
              <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
                <i className="fas fa-book me-1" />Docs
              </button>
              <button className="btn btn-sm btn-success" onClick={handleExport}>
                <i className="fas fa-file-export me-1" />Export
              </button>
            </div>
          </div>
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger m-3">{error}</div>
          ) : (
            <AgGridTable
              ref={gridRef}
              rowData={roles}
              columnDefs={columnDefs}
              onRowClicked={handleRowClick}
              onCellValueChanged={handleCellValueChanged}
            />
          )}
        </div>

      <SidebarPanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        title={isCreateMode ? "Create Sales Role" : "Sales Role Details"}
      >
        {alertStateSidePanel.show && (
          <AlertMessage
            type={alertStateSidePanel.type}
            title={alertStateSidePanel.title}
            message={alertStateSidePanel.message}
            dismissible={true}
            onDismiss={() => setAlertStateSidePanel({ ...alertStateSidePanel, show: false })}
            className="mb-3"
          />
        )}
        <div className="card">
          <div className="card-body p-3">
            {isCreateMode ? (
              <form onSubmit={handleCreateRole}>
                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., SDR, Account Executive, Sales Manager"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Slug *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.slug}
                      onChange={e => setFormData({...formData, slug: e.target.value})}
                      placeholder="e.g., sales_sdr, sales_ae, sales_manager"
                    />
                    <small className="text-muted">Unique identifier (lowercase, underscores)</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Organization Scope</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.organization || ''}
                      onChange={e => setFormData({...formData, organization: e.target.value || null})}
                    >
                      <option value="">Global (All Organizations)</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <small className="text-muted">Leave as Global for system-wide roles, or select an organization for org-specific roles</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Category *</label>
                    <select
                      className="form-select form-select-sm"
                      required
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                    >
                      <option value="org_user">Sales Organization User</option>
                      <option value="other_org_user">Other Organization User</option>
                    </select>
                    <small className="text-muted">org_user for sales team roles, other_org_user for other org roles</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Description</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="3"
                      value={formData.description}
                      onChange={e => setFormData({...formData, description: e.target.value})}
                      placeholder="Detailed description of this sales role..."
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Display Order</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={formData.order}
                      onChange={e => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    />
                    <small className="text-muted">Lower numbers appear first</small>
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.is_active}
                      onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary me-2">
                      Create Role
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClosePanel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            ) : selectedRole && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-primary mb-0">{selectedRole.name}</h5>
                  <span className={`badge bg-${selectedRole.is_active ? 'success' : 'secondary'}`}>
                    {selectedRole.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="row g-2 small mb-3">
                  <div className="col-6">
                    <strong>Slug:</strong><br/>
                    <code>{selectedRole.slug}</code>
                  </div>
                  <div className="col-6">
                    <strong>Scope:</strong><br/>
                    {selectedRole.organization_name ? (
                      <span className="badge bg-primary">{selectedRole.organization_name}</span>
                    ) : (
                      <span className="badge bg-secondary">Global</span>
                    )}
                  </div>
                  <div className="col-6">
                    <strong>Category:</strong><br/>
                    {selectedRole.category === 'org_user' ? 'Sales Organization User' : 'Other Organization User'}
                  </div>
                  <div className="col-6">
                    <strong>Order:</strong><br/>
                    {selectedRole.order}
                  </div>
                  <div className="col-6">
                    <strong>Created:</strong><br/>
                    {new Date(selectedRole.created_at).toLocaleDateString()}
                  </div>
                  <div className="col-12">
                    <strong>Description:</strong><br/>
                    {selectedRole.description || '-'}
                  </div>
                </div>

                <hr />

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedRole.name}
                      onChange={e => setSelectedRole({...selectedRole, name: e.target.value})}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Slug</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedRole.slug}
                      onChange={e => setSelectedRole({...selectedRole, slug: e.target.value})}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Organization Scope</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedRole.organization_id || ''}
                      onChange={e => setSelectedRole({...selectedRole, organization_id: e.target.value || null})}
                    >
                      <option value="">Global (All Organizations)</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Category</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedRole.category}
                      onChange={e => setSelectedRole({...selectedRole, category: e.target.value})}
                    >
                      <option value="org_user">Sales Organization User</option>
                      <option value="other_org_user">Other Organization User</option>
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Description</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="3"
                      value={selectedRole.description || ''}
                      onChange={e => setSelectedRole({...selectedRole, description: e.target.value})}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Order</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={selectedRole.order}
                      onChange={e => setSelectedRole({...selectedRole, order: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedRole.is_active}
                      onChange={e => setSelectedRole({...selectedRole, is_active: e.target.value === 'true'})}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <hr className="my-4" />

                {/* Scope Management Section */}
                <div className="mb-3">
                  <h6 className="text-primary mb-2">OAuth2 Scopes / Permissions</h6>
                  <p className="text-muted small mb-2">
                    Define resource-level permissions for this role using CRUDS notation (Create, Read, Update, Delete, Special).
                  </p>
                  {!showScopeEditor ? (
                    <>
                      {selectedRole.metadata?.scopes ? (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-2">Current Scopes:</small>
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {selectedRole.metadata.scopes.split(' ').map((scope, idx) => (
                              <span key={idx} className="badge bg-info">
                                {scope}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted small mb-2">No scopes assigned to this role.</p>
                      )}
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowScopeEditor(true)}
                      >
                        <i className="fas fa-key me-1"></i>
                        Manage Scopes
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-outline-secondary btn-sm mb-3"
                        onClick={() => setShowScopeEditor(false)}
                      >
                        <i className="fas fa-times me-1"></i>
                        Hide Scope Editor
                      </button>
                      <ScopeEditorForm
                        userData={{
                          id: selectedRole.id,
                          user_type: selectedRole.category,
                          role: selectedRole
                        }}
                        resources={resources}
                        onClose={() => setShowScopeEditor(false)}
                        onSaveSuccess={(message) => {
                          setShowScopeEditor(false);
                          fetchRoles();
                          setAlertStateSidePanel({ show: true, type: 'success', title: 'Success', message: message || 'Scopes updated successfully' });
                        }}
                      />
                    </>
                  )}
                </div>

                <hr className="my-4" />

                <div className="row mt-3">
                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm me-2"
                      onClick={handleUpdateRole}
                    >
                      Save Changes
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleDeleteRole}
                    >
                      Delete Role
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SidebarPanel>

      <ModelDocumentationModal
        show={showDocsModal}
        onHide={() => setShowDocsModal(false)}
        modelType="role"
      />
    </>
  );
};

export default SalesRolesPage;
